import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { fmtDateTime } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PRIORITIES } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Warning, CheckCircle, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Field } from "./Employees";
import { MultiSelect } from "@/components/MultiSelect";

export default function Tasks() {
  const { user } = useAuth();
  const canCreate = hasRole(user, "super_admin", "sales_manager", "department_manager", "hr");
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [commentTask, setCommentTask] = useState(null);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("status") === "overdue" ? "overdue" : (searchParams.get("status") === "completed" ? "completed" : "open");
  const [tab, setTab] = useState(initialTab);
  const empty = { name: "", description: "", priority: "medium", due_date: "", assigned_to: [] };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/tasks").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/employees/lookup/all").then((r) => setEmployees(r.data)); }, []);

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try { await api.post("/tasks", form); toast.success("Created"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const updateTask = async (id, patch) => {
    try { await api.put(`/tasks/${id}`, patch); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const markComplete = async (id) => { await updateTask(id, { progress: 100, status: "completed" }); toast.success("Task completed"); };
  const requestComplete = async (id) => { await updateTask(id, { progress: 100 }); toast.success("Completion sent for manager approval"); };
  const reopenTask = async (id) => { await updateTask(id, { progress: 0, status: "not_started" }); toast.success("Task reopened"); };
  const completionAction = async (id, action) => {
    const comment = window.prompt(`Comment for ${action}`, ""); if (comment === null) return;
    try { await api.post(`/tasks/${id}/completion-action`, { action, comment }); toast.success("Done"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filter = (t) => {
    if (tab === "completed") return t.status === "completed";
    if (tab === "overdue") return t.status === "overdue";
    return t.status !== "completed";
  };
  const view = items.filter(filter);
  const counts = {
    open: items.filter((t) => t.status !== "completed").length,
    overdue: items.filter((t) => t.status === "overdue").length,
    completed: items.filter((t) => t.status === "completed").length,
  };

  return (
    <div data-testid="tasks-page">
      <PageHeader title="Tasks" description="Track work assignments across the organisation." action={canCreate && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setForm(empty)} className="btn-brand" data-testid="task-new"><Plus size={16} className="mr-1.5" />New Task</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Description"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Priority">
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Due Date"><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></Field>
              </div>
              <Field label="Assignees">
                <MultiSelect options={employees.map((e) => ({ value: e.id, label: e.full_name }))} value={form.assigned_to} onChange={(v) => setForm({ ...form, assigned_to: v })} />
              </Field>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="btn-brand" data-testid="task-save">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="open" data-testid="tasks-tab-open">Open ({counts.open})</TabsTrigger>
          <TabsTrigger value="pending_approval" data-testid="tasks-tab-pending">Pending Approval ({counts.pending_approval})</TabsTrigger>
          <TabsTrigger value="overdue" data-testid="tasks-tab-overdue">Overdue ({counts.overdue})</TabsTrigger>
          <TabsTrigger value="completed" data-testid="tasks-tab-completed">Completed ({counts.completed})</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Task</TableHead><TableHead>Priority</TableHead><TableHead>Due</TableHead>
                <TableHead>Assignees</TableHead><TableHead>Progress</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {view.map((t) => {
                  const isAssignee = (t.assigned_to || []).includes(user.id);
                  const isOwner = t.created_by === user.id || user.role === "super_admin";
                  const canEdit = isOwner || isAssignee;
                  const isCompleted = t.status === "completed";
                  return (
                    <TableRow key={t.id} data-testid={`task-row-${t.id}`}>
                      <TableCell>
                        <div className={`font-medium ${isCompleted ? "line-through opacity-60" : ""}`}>{t.name}</div>
                        <div className="text-xs opacity-60 truncate max-w-md">{t.description}</div>
                      </TableCell>
                      <TableCell><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${t.priority === "critical" ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" : t.priority === "high" ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300" : t.priority === "medium" ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>{t.priority}</span></TableCell>
                      <TableCell className="text-sm">{t.due_date && new Date(t.due_date) < new Date() && !isCompleted && <Warning size={14} className="inline text-red-600 mr-1" />}{fmtDate(t.due_date)}</TableCell>
                      <TableCell className="text-xs">{(t.assigned_to || []).map((a) => employees.find((e) => e.id === a)?.full_name).filter(Boolean).join(", ") || "—"}</TableCell>
                      <TableCell className="w-40">
                        {canEdit && !isCompleted ? (
                          <Select value={String(t.progress || 0)} onValueChange={(v) => updateTask(t.id, { progress: Number(v) })}>
                            <SelectTrigger className="h-8" data-testid={`task-progress-${t.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{[0, 25, 50, 75, 100].map((p) => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}</SelectContent>
                          </Select>
                        ) : <Progress value={t.progress || 0} className="h-2" />}
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isCompleted ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" : t.status === "overdue" ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" : t.status === "in_progress" ? "chip-pipeline" : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
                          {(t.status || "not_started").replace(/_/g, " ")}
                        </span>
                      </TableCell>
                      <TableCell className="text-right space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => setCommentTask(t)} data-testid={`task-comments-${t.id}`}>💬</Button>
                        {canEdit && !isCompleted && t.status !== "pending_completion_approval" && (
                          isOwner ? (
                            <Button size="sm" variant="outline" data-testid={`task-complete-${t.id}`} onClick={() => markComplete(t.id)} className="text-green-700 dark:text-green-400">
                              <CheckCircle size={14} className="mr-1" /> Mark Complete
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" data-testid={`task-request-complete-${t.id}`} onClick={() => requestComplete(t.id)}>
                              <CheckCircle size={14} className="mr-1" /> Mark Complete
                            </Button>
                          )
                        )}
                        {t.status === "pending_completion_approval" && isOwner && (
                          <div className="inline-flex gap-1.5">
                            <Button size="sm" className="btn-brand" data-testid={`task-approve-complete-${t.id}`} onClick={() => completionAction(t.id, "approve")}>Approve</Button>
                            <Button size="sm" variant="outline" className="text-red-600" data-testid={`task-reject-complete-${t.id}`} onClick={() => completionAction(t.id, "reject")}>Reject</Button>
                          </div>
                        )}
                        {t.status === "pending_completion_approval" && !isOwner && <span className="text-xs opacity-60">Awaiting manager</span>}
                        {canEdit && isCompleted && (
                          <Button size="sm" variant="outline" data-testid={`task-reopen-${t.id}`} onClick={() => reopenTask(t.id)}>
                            <ArrowCounterClockwise size={14} className="mr-1" /> Reopen
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {view.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm opacity-60 py-10">No tasks.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
      <TaskCommentsDialog task={commentTask} onOpenChange={(o) => !o && setCommentTask(null)} />
    </div>
  );
}

function TaskCommentsDialog({ task, onOpenChange }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!task) return;
    setText("");
    api.get(`/tasks/${task.id}/comments`).then((r) => setComments(r.data)).catch(() => setComments([]));
  }, [task]);
  const add = async () => {
    if (!text.trim()) return toast.error("Write something");
    setBusy(true);
    try {
      const r = await api.post(`/tasks/${task.id}/comments`, { text, progress: task.progress, status: task.status });
      setComments((prev) => [...prev, r.data]);
      setText("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!task} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{task?.name} — updates</DialogTitle></DialogHeader>
        <div className="text-xs opacity-70 mb-2">Progress: <b>{task?.progress || 0}%</b> · Status: <b className="capitalize">{(task?.status || "").replace(/_/g, " ")}</b></div>
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {comments.length === 0 && <div className="text-xs opacity-60 text-center py-6">No updates yet — be the first.</div>}
          {comments.map((c) => (
            <div key={c.id} className="rounded-md border p-2.5 text-sm" style={{ borderColor: "hsl(var(--border))" }} data-testid={`task-comment-${c.id}`}>
              <div className="flex items-baseline justify-between">
                <b>{c.author_name}</b>
                <span className="text-xs opacity-60">{fmtDateTime(c.created_at)}</span>
              </div>
              {(c.progress_snapshot !== null && c.progress_snapshot !== undefined) && (
                <div className="text-[10px] uppercase tracking-wider opacity-60">Progress {c.progress_snapshot}%</div>
              )}
              <div className="mt-1 whitespace-pre-wrap">{c.text}</div>
            </div>
          ))}
        </div>
        <Textarea placeholder="Post an update on this task…" value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid="task-comment-text" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button className="btn-brand" onClick={add} disabled={busy} data-testid="task-comment-post">{busy ? "Posting…" : "Post update"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

